import React, { useCallback, useContext, useState } from 'react';
import { z } from 'zod';

import Form from 'components/shared/Form';

import { ItemContext } from 'components/shared/context/ItemContextProvider';
import { ToastContext } from 'components/shared/context/ToastContextProvider';
import { UserContext } from 'components/shared/context/UserContextProvider';

import ValidatedBaseInput from 'components/shared/inputs/ValidatedBaseInput';

import BaseModal from 'components/shared/modals/BaseModal';

import sansanTech from 'services/sansantech';

interface AddCategoryModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const { userState } = useContext(UserContext);
  const { toastDispatch } = useContext(ToastContext);
  const { item, setItem } = useContext(ItemContext);

  const [submitting, setSubmitting] = useState(false);

  const [categoryNameValue, setCategoryNameValue] = useState<string>('');
  const [isCategoryNameValueValid, setIsCategoryNameValueValid] =
    useState<boolean>(false);

  const reset = useCallback(() => {
    setCategoryNameValue('');
    setIsCategoryNameValueValid(false);
  }, []);

  const onSubmit = useCallback(() => {
    setSubmitting(true);
    if (userState.token && item) {
      sansanTech
        .createCategory(userState.token, item.id, { name: categoryNameValue })
        .then((response) => {
          console.log(response);
          setItem?.((currentItem) => {
            const newCategory = response.data;
            if (currentItem) {
              currentItem.categories = currentItem.categories
                ? [...currentItem.categories, newCategory]
                : [newCategory];
            }
            return currentItem;
          });
          setSubmitting(false);
          toastDispatch?.({
            type: 'SUCCESS',
            message: 'Added category',
          });
          setIsOpen(false);
          reset();
        })
        .catch((error) => {
          console.log(error);
          setSubmitting(false);
          toastDispatch?.({
            type: 'ERROR',
            message: 'Could not add category',
          });
        });
    } else {
      setSubmitting(false);
      toastDispatch?.({ type: 'ERROR', message: 'No permission to do this' });
    }
  }, [
    categoryNameValue,
    reset,
    toastDispatch,
    userState.token,
    setIsOpen,
    item,
    setItem,
  ]);

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Add category"
      onClose={reset}
    >
      <Form
        header="Add category"
        onSubmit={onSubmit}
        submitting={submitting}
        disabled={!isCategoryNameValueValid}
        submitButtonText="Add category"
        submitButtonWidth="140px"
      >
        <ValidatedBaseInput
          label="category name"
          type="text"
          value={categoryNameValue}
          setValue={setCategoryNameValue}
          setIsValid={setIsCategoryNameValueValid}
          validators={z.string().min(1, { message: 'This field is required' })}
        />
      </Form>
    </BaseModal>
  );
};

export default AddCategoryModal;
